import { useCallback, useEffect, useMemo, useState } from "react"
import { Collapse } from "components/ui/collapse/Collapse"
import { useCollapseCompetitors } from "hooks/collapseCompetitors.hook"
import {
	useDeleteCompetitor,
	useEditCompetitor,
	useEditCompetitorModelNumbers,
} from "hooks/api/competitors/competitors.hook"
import { DefaultCompetitorModal } from "./layout/DefaultCompetitorModal/DefaultCompetitorModal"
import { DeleteCompetitorModal } from "./layout/DeleteCompetitorModal/DeleteCompetitorModal"
import { EditCompetitorModelNumbersModal } from "./layout/EditCompetitorModelNumbersModal/EditCompetitorModelNumbersModal"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import { useQueryClient } from "@tanstack/react-query"
import { useDeleteModelNumber, useEditModelNumber } from "hooks/api/modelNumbers/modelNumbers.hook"
import { useMessage } from "hooks/message.hook"
import { Skeleton } from "components/ui/skeleton/Skeleton"

type Props = {
	/** Text the rows are filtered by; empty shows everything. */
	searchTerm?: string
	/** Reports how many rows match, so the page can show a hint next to the search box. */
	onResultsChange?: (result: { matches: number; total: number }) => void
}

type CompetitorModalState = {
	isEditModalOpen: boolean
	isDeleteModalOpen: boolean
	competitorId?: number
	competitorName?: string
}

type StevensModelModalState = {
	isEditModalOpen: boolean
	isDeleteModalOpen: boolean
	stevensId?: number
	stevensModelNumber?: string
}

type CompetitorModelNumbersModalState = {
	isOpen: boolean
	competitorId: number
	competitorName: string
	stevensId: number
	stevensModelNumber: string
	modelNumbers: string[]
}

export const CompetitorsCollapse = ({ searchTerm = "", onResultsChange }: Props) => {
	const { formatError } = useApiErrorFormatter()
	const queryClient = useQueryClient()
	const { success: showSuccessMessage } = useMessage()

	const { mutate: deleteCompetitorMutation } = useDeleteCompetitor()
	const { mutate: editCompetitorMutation } = useEditCompetitor()
	const { mutate: editCompetitorModelNumbersMutation } = useEditCompetitorModelNumbers()
	const { mutate: editModelNumberMutation } = useEditModelNumber()
	const { mutate: deleteModelNumberMutation } = useDeleteModelNumber()

	const [competitorModalState, setCompetitorModalState] = useState<CompetitorModalState>({
		isEditModalOpen: false,
		isDeleteModalOpen: false,
		competitorId: undefined,
		competitorName: undefined,
	})

	const [stevensModalState, setStevensModalState] = useState<StevensModelModalState>({
		isEditModalOpen: false,
		isDeleteModalOpen: false,
		stevensId: undefined,
		stevensModelNumber: undefined,
	})

	const [competitorModelNumbersModalState, setCompetitorModelNumbersModalState] =
		useState<CompetitorModelNumbersModalState>({
			isOpen: false,
			competitorId: 0,
			competitorName: "",
			stevensId: 0,
			stevensModelNumber: "",
			modelNumbers: [],
		})

	const onSaveEditCompetitorHandle = (name: string) => {
		if (competitorModalState.competitorId && name.trim().length > 0) {
			editCompetitorMutation(
				{ id: competitorModalState.competitorId, name: name.trim() },
				{
					onSuccess: () => {
						setCompetitorModalState((prev) => ({ ...prev, isEditModalOpen: false }))
						queryClient.invalidateQueries({ queryKey: ["getCompetitors"] })
						queryClient.invalidateQueries({ queryKey: ["competitorsModelNumbers"] })
						showSuccessMessage("Competitor edited successfully")
					},
					onError: (e) => {
						formatError(e)
					},
				},
			)
		}
	}

	const onDeleteCompetitorHandle = () => {
		if (competitorModalState.competitorId) {
			deleteCompetitorMutation(competitorModalState.competitorId, {
				onSuccess: () => {
					setCompetitorModalState((prev) => ({ ...prev, isDeleteModalOpen: false }))
					queryClient.invalidateQueries({ queryKey: ["getCompetitors"] })
					showSuccessMessage("Competitor deleted successfully")
				},
				onError: (e) => {
					formatError(e)
				},
			})
		}
	}

	const onSaveEditStevensModelHandle = (modelNumber: string) => {
		if (stevensModalState.stevensId && modelNumber.trim().length > 0) {
			editModelNumberMutation(
				{ modelNumber: modelNumber, id: stevensModalState.stevensId },
				{
					onSuccess: () => {
						queryClient.invalidateQueries({ queryKey: ["competitorsModelNumbers"] })
						showSuccessMessage("Stevens model number edited successfully")
					},
					onError: (e) => {
						formatError(e)
					},
				},
			)
			setStevensModalState((prev) => ({ ...prev, isEditModalOpen: false }))
		}
	}

	const onDeleteStevensModelHandle = () => {
		if (stevensModalState.stevensId) {
			deleteModelNumberMutation(stevensModalState.stevensId, {
				onSuccess: () => {
					queryClient.invalidateQueries({ queryKey: ["competitorsModelNumbers"] })
					showSuccessMessage("Stevens model number deleted successfully")
				},
				onError: (e) => {
					formatError(e)
				},
			})
			setStevensModalState((prev) => ({ ...prev, isDeleteModalOpen: false }))
		}
	}

	const onSaveCompetitorModelNumbersHandle = (modelNumbers: string[]) => {
		editCompetitorModelNumbersMutation(
			{
				competitorId: competitorModelNumbersModalState.competitorId,
				modelNumbers,
				stevensId: competitorModelNumbersModalState.stevensId,
			},
			{
				onSuccess: () => {
					setCompetitorModelNumbersModalState((prev) => ({ ...prev, isOpen: false }))
					queryClient.invalidateQueries({ queryKey: ["competitorsModelNumbers"] })
					showSuccessMessage("Competitor model numbers updated successfully")
				},
				onError: (e) => {
					formatError(e)
				},
			},
		)
	}

	// The three handlers below are passed into useCollapseCompetitors, which uses them
	// to build the (very large) table columns. They must be referentially stable or the
	// memoization there collapses and every render rebuilds thousands of rows — that was
	// worth ~1.9s of blocked main thread per modal open. They only call state setters,
	// which React guarantees are stable, so an empty dependency list is correct.
	const onEditCompetitorModelNumbers = useCallback(
		(
			competitorId: number,
			competitorName: string,
			stevensId: number,
			stevensModelNumber: string,
			modelNumbers: string[],
		) => {
			setCompetitorModelNumbersModalState({
				isOpen: true,
				competitorId,
				competitorName,
				stevensId,
				stevensModelNumber,
				modelNumbers,
			})
		},
		[],
	)

	const getCompetitorDropdownItems = useCallback((competitorId: number, competitorName: string) => {
		return [
			{
				key: "edit",
				label: "Edit name",
				onClick: () => {
					setCompetitorModalState({
						isEditModalOpen: true,
						isDeleteModalOpen: false,
						competitorId,
						competitorName,
					})
				},
			},
			{
				key: "delete",
				label: "Delete",
				danger: true,
				onClick: () => {
					setCompetitorModalState({
						isEditModalOpen: false,
						isDeleteModalOpen: true,
						competitorId,
						competitorName,
					})
				},
			},
		]
	}, [])

	const getStevensModelDropdownItems = useCallback((stevensId: number, stevensModelNumber: string) => {
		return [
			{
				key: "edit",
				label: "Edit",
				onClick: () => {
					setStevensModalState({
						isEditModalOpen: true,
						isDeleteModalOpen: false,
						stevensId,
						stevensModelNumber,
					})
				},
			},
			{
				key: "delete",
				label: "Delete",
				danger: true,
				onClick: () => {
					setStevensModalState({
						isEditModalOpen: false,
						isDeleteModalOpen: true,
						stevensId,
						stevensModelNumber,
					})
				},
			},
		]
	}, [])

	const { generateCollapseItems, competitorsPending, matches, totalModelNumbers, matchedTopLevelKeys } =
		useCollapseCompetitors({
			getCompetitorDropdownItems,
			getStevensModelDropdownItems,
			onEditCompetitorModelNumbers,
			searchTerm,
		})

	useEffect(() => {
		onResultsChange?.({ matches, total: totalModelNumbers })
	}, [matches, totalModelNumbers, onResultsChange])
	// Memoized so the collapse children (and the tables they hold) keep a stable
	// identity across renders — rebuilding them on every render is what made the
	// page freeze for seconds on any interaction.
	const collapseItems = useMemo(() => generateCollapseItems(), [generateCollapseItems])

	const isSearching = Boolean(searchTerm.trim())

	if (competitorsPending) {
		return <Skeleton rows={8} />
	}

	return (
		<>
			<Collapse
				key={isSearching ? `search-${searchTerm}` : "all"}
				items={collapseItems}
				defaultActiveKey={isSearching ? matchedTopLevelKeys : undefined}
			/>

			{/* Competitor Modals */}
			<DefaultCompetitorModal
				isModalOpen={competitorModalState.isEditModalOpen}
				setModalClose={() => setCompetitorModalState((prev) => ({ ...prev, isEditModalOpen: false }))}
				title="Edit name"
				inputLabel="Competitor name"
				validationMessage="Please enter competitor name"
				cancelButtonText="Cancel"
				saveButtonText="Save"
				onSave={onSaveEditCompetitorHandle}
				initialValue={competitorModalState.competitorName}
			/>
			<DeleteCompetitorModal
				isModalOpen={competitorModalState.isDeleteModalOpen}
				setModalClose={() => setCompetitorModalState((prev) => ({ ...prev, isDeleteModalOpen: false }))}
				title={`Delete ${competitorModalState.competitorName}?`}
				description="Deleting this competitor will also permanently remove all associated model numbers."
				cancelButtonText="Cancel"
				deleteButtonText="Delete"
				onDelete={onDeleteCompetitorHandle}
			/>
			<EditCompetitorModelNumbersModal
				isModalOpen={competitorModelNumbersModalState.isOpen}
				setModalClose={() => setCompetitorModelNumbersModalState((prev) => ({ ...prev, isOpen: false }))}
				competitorName={competitorModelNumbersModalState.competitorName}
				stevensModelNumber={competitorModelNumbersModalState.stevensModelNumber}
				modelNumbers={competitorModelNumbersModalState.modelNumbers}
				onSave={onSaveCompetitorModelNumbersHandle}
			/>

			{/* Stevens Model Modals */}
			<DefaultCompetitorModal
				isModalOpen={stevensModalState.isEditModalOpen}
				setModalClose={() => setStevensModalState((prev) => ({ ...prev, isEditModalOpen: false }))}
				title="Edit Stevens model number"
				inputLabel="Model number"
				validationMessage="Please enter model number"
				cancelButtonText="Cancel"
				saveButtonText="Save"
				onSave={onSaveEditStevensModelHandle}
				initialValue={stevensModalState.stevensModelNumber}
			/>
			<DeleteCompetitorModal
				isModalOpen={stevensModalState.isDeleteModalOpen}
				setModalClose={() => setStevensModalState((prev) => ({ ...prev, isDeleteModalOpen: false }))}
				title={`Delete ${stevensModalState.stevensModelNumber}?`}
				description="Deleting this Stevens model will permanently remove it and all associated competitor model numbers."
				cancelButtonText="Cancel"
				deleteButtonText="Delete"
				onDelete={onDeleteStevensModelHandle}
			/>
		</>
	)
}
